import { inject } from '@angular/core';
import * as AlertActions from '@curbnturf/alert/src/lib/+state/alert.actions';
import * as AuthSelectors from '@curbnturf/auth/src/lib/+state/auth.selectors';
import { IListing, IPhoto, ISubPhoto, Listing, NGRX_NO_ACTION } from '@curbnturf/entities';
import { stringFromError } from '@curbnturf/form-helpers';
import { OfflineListingDbSet } from '@curbnturf/network/src/lib/native/dbset/offline-listing.dbset';
import * as NetworkActions from '@curbnturf/network/src/lib/network/+state/network.actions';
import * as NetworkSelectors from '@curbnturf/network/src/lib/network/+state/network.selectors';
import { CaretakerService } from '@curbnturf/rating';
import * as SiteActions from '@curbnturf/site/src/lib/+state/site.actions';
import { GlobalSpinnerService } from '@curbnturf/ui/src/lib/spinner/global-spinner.service';
import * as UserActions from '@curbnturf/user/src/lib/+state/user.actions';
import * as UserSelectors from '@curbnturf/user/src/lib/+state/user.selectors';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { ListingService } from '../listing.service';
import { OfflineListingService } from '../offline-listing.service';
import * as ListingActions from './listing.actions';
import * as ListingSelectors from './listing.selectors';

export const init$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ListingActions.init),
      withLatestFrom(store.select(ListingSelectors.getListings)),
      map(([action, listings]) => {
        if (listings?.length) {
          return NGRX_NO_ACTION;
        }

        return ListingActions.loadListings({});
      }),
    ),
  { functional: true },
);

export const createListing$ = createEffect(
  (
    actions$ = inject(Actions),
    listingService = inject(ListingService),
    store = inject(Store),
    offlineListingService = inject(OfflineListingService),
  ) =>
    actions$.pipe(
      ofType(ListingActions.createListing),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      switchMap(([[action, status], currentUser]) => {
        const listing: IListing = { ...action.listing, user: currentUser };
        if (status.connected) {
          return listingService.create(listing).pipe(
            mergeMap((createdListing: IListing) => [
              ListingActions.addNewSite({ listing: createdListing }),
              ListingActions.listingCreated({ listing: createdListing }),
              ListingActions.listingLoaded({ listing: createdListing }),
              ListingActions.selectListing({ listingId: createdListing.id }),
              UserActions.setInProgressListing({ listing: createdListing, user: currentUser }),
            ]),
            catchError((err) =>
              of(
                ListingActions.createListingFailed({
                  error: err?.error?.message || err?.message || err || 'Error Creating Listing',
                }),
              ),
            ),
          );
        } else {
          // If Offline, use the alternate
          return offlineListingService.create(listing).pipe(
            mergeMap((createdListing: IListing) => [
              ListingActions.addNewSite({ listing: createdListing }),
              ListingActions.listingCreated({ listing: createdListing }),
              ListingActions.listingLoaded({ listing: createdListing }),
              ListingActions.selectListing({ listingId: createdListing.syncId }),
              UserActions.setInProgressListing({ listing: createdListing, user: currentUser }),
            ]),
            catchError((err) =>
              of(
                ListingActions.createListingFailed({
                  error: err?.error?.message || err?.message || err || 'Error Creating Listing',
                }),
              ),
            ),
          );
        }
      }),
    ),
  { functional: true },
);

export const removeImageFromListing$ = createEffect(
  (actions$ = inject(Actions), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.removeImageFromListing),
      switchMap((action) =>
        listingService.removeImage(action.listingId, action.index).pipe(
          mergeMap((photos: IPhoto[]) => {
            return [
              ListingActions.listingImagesUpdated({ id: action.listingId, photos }),
              AlertActions.displayAlert({
                title: 'Image Removed from Listing',
                body: 'Removed Image',
                level: 'error',
              }),
            ];
          }),
          catchError((err) =>
            of(
              ListingActions.createListingFailed({
                error: err?.error?.message || err?.message || err || 'Error Removing Image From Listing',
              }),
            ),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const removeListing$ = createEffect(
  (
    actions$ = inject(Actions),
    listingService = inject(ListingService),
    store = inject(Store),
    offlineListingService = inject(OfflineListingService),
  ) =>
    actions$.pipe(
      ofType(ListingActions.removeListing),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      switchMap(([action, status]) => {
        if (status.connected) {
          return listingService.remove(action.listingId as number).pipe(
            switchMap((removedListing: IListing) => {
              const actionArray: Action[] = [
                ListingActions.listingRemoved({
                  listing: removedListing,
                }),
              ];

              if (removedListing) {
                actionArray.push(
                  AlertActions.displayAlert({
                    title: 'Listing removed',
                    body: 'Removed Listing',
                    level: 'error',
                  }),
                );
              }

              return actionArray;
            }),
            catchError((err) => [
              ListingActions.removeListingFailed({
                error: err?.error?.message || err?.message || err || 'Error Removing Listing',
              }),
            ]),
          );
        } else if (!status.connected) {
          return offlineListingService.remove(action.listingId as string).pipe(
            switchMap((removedListing: IListing) => {
              const actionArray: Action[] = [
                ListingActions.listingRemoved({
                  listing: removedListing,
                }),
              ];

              if (removedListing) {
                actionArray.push(
                  AlertActions.displayAlert({
                    title: 'Listing removed',
                    body: 'Removed Listing',
                    level: 'error',
                  }),
                );
              }

              return actionArray;
            }),
            catchError((err) => [
              ListingActions.removeListingFailed({
                error: err?.error?.message || err?.message || err || 'Error Removing Listing',
              }),
            ]),
          );
        }

        return of(
          ListingActions.removeListingFailed({
            error: 'Error Removing Listing - Could Not Match Connection State',
          }),
        );
      }),
      catchError((err) =>
        of(
          ListingActions.removeListingFailed({
            error:
              err?.error?.message || err?.message || err || 'Error Removing Listing - Could Not Match Connection State',
          }),
        ),
      ),
    ) as Observable<Action>,
  { functional: true },
);

export const restoreListing$ = createEffect(
  (actions$ = inject(Actions), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.restoreListing),
      switchMap((action) =>
        listingService.restore(action.listingId).pipe(
          switchMap((restoredListing: IListing) => {
            const actionArray: Action[] = [ListingActions.listingRestored({ listing: restoredListing })];

            if (restoredListing) {
              actionArray.push(
                AlertActions.displayAlert({
                  title: 'Listing Restored',
                  body: 'Restored Listing',
                  level: 'error',
                }),
              );
            }

            return actionArray;
          }),
          catchError((err) =>
            of(
              ListingActions.createListingFailed({
                error: err?.error?.message || err?.message || err || 'Error Restoring Listing',
              }),
            ),
          ),
        ),
      ),
      catchError((err) =>
        of(
          ListingActions.createListingFailed({
            error: err?.error?.message || err?.message || err || 'Error Restoring Listing',
          }),
        ),
      ),
    ) as Observable<Action>,
  { functional: true },
);

export const loadListing$ = createEffect(
  (
    actions$ = inject(Actions),
    listingService = inject(ListingService),
    store = inject(Store),
    offlineListingService = inject(OfflineListingService),
  ) =>
    actions$.pipe(
      ofType(ListingActions.loadListing),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      switchMap(([action, status]) => {
        if (status.connected && typeof action.listingId === 'number') {
          return listingService.fetch(action.listingId).pipe(
            switchMap((listing: IListing) => [
              ListingActions.listingLoaded({ listing }),
              ListingActions.selectListing({ listingId: listing.id }),
            ]),
            catchError((err) =>
              of(
                ListingActions.loadListingFailed({
                  error: err?.error?.message || err?.message || err || 'Error Loading Listing',
                }),
              ),
            ),
          );
        } else if (typeof action.listingId === 'string') {
          return offlineListingService.fetch(action.listingId).pipe(
            switchMap((listing: IListing) => [
              ListingActions.listingLoaded({ listing }),
              ListingActions.selectListing({ listingId: listing.syncId }),
            ]),
            catchError((err) =>
              of(
                ListingActions.loadListingFailed({
                  error: err?.error?.message || err?.message || err || 'Error Loading Listing',
                }),
              ),
            ),
          );
        } else {
          return of(NGRX_NO_ACTION);
        }
      }),
      catchError((err) =>
        of(
          ListingActions.loadListingFailed({
            error: err?.error?.message || err?.message || err || 'Error Loading Listing',
          }),
        ),
      ),
    ),
  { functional: true },
);

export const loadListings$ = createEffect(
  (actions$ = inject(Actions), listingService = inject(ListingService), store = inject(Store)) =>
    actions$.pipe(
      ofType(ListingActions.loadListings),
      switchMap((action) => {
        if (action.payload) {
          return listingService.fetchAll(action.payload).pipe(
            switchMap((result: { total: number; page: number; listings: IListing[] }) => [
              ListingActions.listingsLoaded({ listings: result.listings }),
            ]),
            catchError((err) =>
              of(
                ListingActions.loadListingFailed({
                  error: err?.error?.message || err?.message || err || 'Error Loading Listings',
                }),
              ),
            ),
          );
        }

        return store.pipe(select(UserSelectors.getCurrentId)).pipe(
          filter((userId) => !!userId),
          take(1),
          switchMap((userId) => {
            if (!userId) {
              return [ListingActions.loadListingFailed({ error: 'No user id provided' })];
            }

            return listingService.fetchAll({ userId }).pipe(
              switchMap((result: { total: number; page: number; listings: IListing[] }) => [
                ListingActions.listingsLoaded({ listings: result.listings }),
              ]),
              catchError((err) => [
                ListingActions.loadListingFailed({ error: err?.error?.message || err || 'Error Loading Listings' }),
              ]),
            );
          }),
          catchError((err) => [
            ListingActions.loadListingFailed({
              error: err?.error?.message || err?.message || err || 'Error Loading Listings',
            }),
          ]),
        );
      }),
      catchError((err) => [
        ListingActions.loadListingFailed({ error: err?.error?.message || err || 'Error Loading Listings' }),
      ]),
    ),
  { functional: true },
);

// using mergeMap to allow multiple updates at a time
export const updateListing$ = createEffect(
  (actions$ = inject(Actions), listingService = inject(ListingService), store = inject(Store)) =>
    actions$.pipe(
      ofType(ListingActions.updateListing),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([action, status]) => {
        if (status.connected) {
          return listingService.update(action.listing).pipe(
            mergeMap((updatedListing: IListing) => [ListingActions.listingLoaded({ listing: updatedListing })]),
            catchError((err) => [
              ListingActions.updateListingFailed({ error: err?.error?.message || err || 'Error Updating Listing' }),
            ]),
          );
        } else {
          // If Offline, we handle this elsewhere
          return [NGRX_NO_ACTION];
        }
      }),
      catchError((err) => [
        ListingActions.updateListingFailed({ error: err?.error?.message || err || 'Error Updating Listing' }),
      ]),
    ),
  { functional: true },
);

// using mergeMap to allow multiple updates at a time
export const updateListingOffline$ = createEffect(
  (actions$ = inject(Actions), offlineListingDbSet = inject(OfflineListingDbSet)) =>
    actions$.pipe(
      ofType(ListingActions.updateListingOffline),
      mergeMap((action) => {
        return of(offlineListingDbSet.store(action.payload)).pipe(
          mergeMap(() => [NGRX_NO_ACTION]),
          catchError((err) => [
            ListingActions.updateListingFailed({ error: err?.error?.message || err || 'Error Updating Listing' }),
          ]),
        );
      }),
      catchError((err) => [
        ListingActions.updateListingFailed({ error: err?.error?.message || err || 'Error Updating Listing' }),
      ]),
    ),
  { functional: true },
);

export const enableListing$ = createEffect(
  (actions$ = inject(Actions), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.enableListing),
      switchMap((action) => {
        if (action.listing) {
          return listingService.enable(action.listing).pipe(
            mergeMap((result: { success: boolean; message: string }) => {
              if (result.success && action.listing?.id) {
                return [
                  AlertActions.displayAlert({
                    title: 'Listing Published',
                    body: `Published ${action.listing.name}`,
                  }),
                  ListingActions.listingEnabled({ listing: { ...action.listing } }),
                ];
              }
              return [
                ListingActions.listingEnabledFailed({
                  body: `Unable to publish listing for the following reasons:\n${result.message}`,
                  listingId: action.listing.id,
                }),
              ];
            }),
            catchError((err) => [
              ListingActions.listingEnabledFailed({
                body: err?.error?.message || err?.message || err || 'Error Enabling Listing',
              }),
            ]),
          );
        } else {
          return [
            ListingActions.listingEnabledFailed({
              body: 'Listing Record Not Provided',
            }),
          ];
        }
      }),
      catchError((err) => [
        ListingActions.listingEnabledFailed({
          body: err?.error?.message || err?.message || err || 'Error Enabling Listing',
        }),
      ]),
    ),
  { functional: true },
);

export const disableListing$ = createEffect(
  (actions$ = inject(Actions), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.disableListing),
      switchMap((action) =>
        listingService.disable(action.listing).pipe(
          mergeMap((result: { success: boolean; message: string }) => {
            if (result.success && action.listing.id) {
              return [
                AlertActions.displayAlert({
                  title: 'Listing Disabled',
                  body: `Disabled ${action.listing.name}`,
                }),
                ListingActions.listingDisabled({
                  listing: action.listing,
                }),
              ];
            }
            return [
              ListingActions.listingDisabledFailed({
                error: `Unable to disable listing for the following reasons:\n${result.message}`,
              }),
            ];
          }),
          catchError((err) => [
            ListingActions.listingDisabledFailed({
              error: err?.error?.message || err?.message || err || 'Error Disabling Listing',
            }),
          ]),
        ),
      ),
      catchError((err) => [
        ListingActions.listingEnabledFailed({
          body: err?.error?.message || err?.message || err || 'Error Enabling Listing',
        }),
      ]),
    ),
  { functional: true },
);

export const setFeaturedListing$ = createEffect(
  (actions$ = inject(Actions), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.setFeaturedListing),
      switchMap((action) =>
        listingService.setFeatured(action.listing, action.featured).pipe(
          mergeMap((listing: IListing) => [
            AlertActions.displayAlert({
              title: action.featured ? 'Listing Featured' : 'Listing Unfeatured',
              body: `${action.featured ? 'Featured' : 'Removed from featured'} ${action.listing.name}`,
            }),
            ListingActions.listingFeaturedSet({ listing }),
          ]),
          catchError((err) => [
            ListingActions.listingFeaturedSetFailed({
              error: err?.error?.message || err?.message || err || 'Error Setting Featured',
            }),
          ]),
        ),
      ),
      catchError((err) => [
        ListingActions.listingFeaturedSetFailed({
          error: err?.error?.message || err?.message || err || 'Error Setting Featured',
        }),
      ]),
    ),
  { functional: true },
);

export const createListingPolicy$ = createEffect(
  (actions$ = inject(Actions), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.createPolicy),
      switchMap((action) =>
        listingService.createPolicy(action.listing).pipe(
          mergeMap((updatedListing: IListing) => [
            ListingActions.listingLoaded({
              listing: updatedListing,
            }),
          ]),
          catchError((err) => [
            ListingActions.updateListingFailed({
              error: err?.error?.message || err?.message || err || 'Error Creating Listing Policy',
            }),
          ]),
        ),
      ),
      catchError((err) => [
        ListingActions.updateListingFailed({
          error: err?.error?.message || err?.message || err || 'Error Creating Listing Policy',
        }),
      ]),
    ),
  { functional: true },
);

export const removeListingPolicy$ = createEffect(
  (actions$ = inject(Actions), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.removePolicy),
      switchMap((action) =>
        listingService.removePolicy(action.listing, action.policy).pipe(
          mergeMap((updatedListing: IListing) => [
            ListingActions.listingLoaded({
              listing: updatedListing,
            }),
          ]),
          catchError((err) => [
            ListingActions.updateListingFailed({
              error: err?.error?.message || err?.message || err || 'Error Removing Listing Policy',
            }),
          ]),
        ),
      ),
      catchError((err) => [
        ListingActions.updateListingFailed({
          error: err?.error?.message || err?.message || err || 'Error Removing Listing Policy',
        }),
      ]),
    ),
  { functional: true },
);

export const createListingSignalReception$ = createEffect(
  (actions$ = inject(Actions), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.createSignalReception),
      switchMap((action) =>
        listingService.createSignalReception(action.listing).pipe(
          mergeMap((updatedListing: IListing) => [
            ListingActions.listingLoaded({
              listing: updatedListing,
            }),
          ]),
          catchError((err) => [
            ListingActions.updateListingFailed({
              error: err?.error?.message || err?.message || err || 'Error Creating Listing Signal Reception',
            }),
          ]),
        ),
      ),
      catchError((err) => [
        ListingActions.updateListingFailed({
          error: err?.error?.message || err?.message || err || 'Error Creating Listing Signal Reception',
        }),
      ]),
    ),
  { functional: true },
);

export const removeListingSignalReception$ = createEffect(
  (actions$ = inject(Actions), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.removeSignalReception),
      switchMap((action) =>
        listingService.removeSignalReception(action.listing, action.signalReception).pipe(
          mergeMap((updatedListing: IListing) => [
            ListingActions.listingLoaded({
              listing: updatedListing,
            }),
          ]),
          catchError((err) => [
            ListingActions.updateListingFailed({
              error: err?.error?.message || err?.message || err || 'Error Removing Listing Signal Reception',
            }),
          ]),
        ),
      ),
      catchError((err) => [
        ListingActions.updateListingFailed({
          error: err?.error?.message || err?.message || err || 'Error Removing Listing Signal Reception',
        }),
      ]),
    ),
  { functional: true },
);

export const uploadListingImage$ = createEffect(
  (
    actions$ = inject(Actions),
    listingService = inject(ListingService),
    store = inject(Store),
    spinnerService = inject(GlobalSpinnerService),
    offlineListingService = inject(OfflineListingService),
  ) =>
    actions$.pipe(
      ofType(ListingActions.uploadListingImage),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      switchMap(([action, status]) => {
        const payload = action;
        if (!payload || !payload.formData || !payload.data || (!payload.data.listingId && !payload.data.syncId)) {
          return of(NGRX_NO_ACTION);
        }
        if (status.connected && payload.data.listingId) {
          const files = payload.formData;
          const listingId = payload.data.listingId;
          spinnerService.spinnerOn();
          return listingService.uploadImage(listingId, files).pipe(
            switchMap((results: { id: number; photos: IPhoto[] }) => {
              spinnerService.spinnerOff();
              return [
                ListingActions.listingImagesUpdated({
                  ...results,
                }),
              ];
            }),
            catchError((err) => {
              spinnerService.spinnerOff();

              return [
                ListingActions.createListingFailed({
                  error: err?.error?.message || err?.message || err || 'Error Uploading Listing Image',
                }),
              ];
            }),
          );
        } else if (!status.connected && payload.data.syncId) {
          const files = payload.formData;
          const syncId = payload.data.syncId;
          spinnerService.spinnerOn();
          return offlineListingService.uploadImage(syncId, files).pipe(
            switchMap((results: { syncId: string; photos: IPhoto[] }) => {
              spinnerService.spinnerOff();
              return [
                ListingActions.listingImagesUpdated({
                  ...results,
                }),
              ];
            }),
            catchError((err) => {
              spinnerService.spinnerOff();

              return [
                ListingActions.createListingFailed({
                  error: err?.error?.message || err?.message || err || 'Error Uploading Listing Image',
                }),
              ];
            }),
          );
        } else {
          return of(NGRX_NO_ACTION);
        }
      }),
      catchError((err) => {
        spinnerService.spinnerOff();

        return [
          ListingActions.createListingFailed({
            error: err?.error?.message || err?.message || err || 'Error Uploading Listing Image',
          }),
        ];
      }),
    ),
  { functional: true },
);

export const recropListingImage$ = createEffect(
  (actions$ = inject(Actions), listingService = inject(ListingService), store = inject(Store)) =>
    actions$.pipe(
      ofType(ListingActions.recropImage),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      switchMap(([action, status]) => {
        if (!action || !action.listingToSave || !action.listingToSave.photos || !action.photo) {
          return of(NGRX_NO_ACTION);
        }

        const listing: IListing = { ...action.listingToSave, photos: [...action.listingToSave.photos] };
        const photo: ISubPhoto = action.photo;
        const foundPhotoIndex = (listing.photos || []).findIndex((el) => el.key === photo.key);
        if (foundPhotoIndex >= 0 && listing.photos) {
          const newPhoto = { ...photo, key: listing.photos[foundPhotoIndex].key };
          listing.photos.splice(foundPhotoIndex, 1, newPhoto);
        } else {
          return of(ListingActions.recropImageFailure({ error: 'Failed to find the image to crop.' }));
        }

        if (status.connected && listing.id) {
          return listingService.update(listing).pipe(
            mergeMap((updatedListing: IListing) => [
              ListingActions.listingLoaded({ listing: updatedListing }),
              ListingActions.listingImagesUpdated({ id: updatedListing.id, photos: updatedListing.photos ?? [] }),
            ]),
            catchError((err) => [
              ListingActions.updateListingFailed({ error: err?.error?.message || err || 'Error Updating Listing' }),
            ]),
          );
        } else {
          return of(NGRX_NO_ACTION);
        }
      }),
      catchError((err) => {
        return [
          ListingActions.createListingFailed({
            error: err?.error?.message || err?.message || err || 'Error Uploading Listing Image',
          }),
        ];
      }),
    ),
  { functional: true },
);

export const uploadBoondockReviewImage$ = createEffect(
  (
    actions$ = inject(Actions),
    caretakerService = inject(CaretakerService),
    spinnerService = inject(GlobalSpinnerService),
  ) =>
    actions$.pipe(
      ofType(ListingActions.uploadBoondockReviewImage),
      switchMap((action) => {
        if (!action || !action.formData || !action.data || !action.data.listingId) {
          return of(NGRX_NO_ACTION);
        }
        const files = action.formData;
        const listingId = action.data.listingId;
        spinnerService.spinnerOn();
        return caretakerService.uploadImage(listingId, files).pipe(
          switchMap((results: { id: number; photos: IPhoto[] }) => {
            spinnerService.spinnerOff();
            return [
              ListingActions.boondockReviewImageUploaded({
                ...results,
              }),
            ];
          }),
          catchError((err) => {
            spinnerService.spinnerOff();

            return [
              ListingActions.boondockReviewImageUploadFailed({
                error: err?.error?.message || err?.message || err || 'Error Uploading Boondock Review Image',
              }),
            ];
          }),
        );
      }),
      catchError((err) => {
        spinnerService.spinnerOff();

        return [
          ListingActions.boondockReviewImageUploadFailed({
            error: err?.error?.message || err?.message || err || 'Error Uploading Boondock Review Image',
          }),
        ];
      }),
    ),
  { functional: true },
);

export const selectListing$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ListingActions.selectListing),
      withLatestFrom(store.select(ListingSelectors.getListings)),
      switchMap(([action, listings]) => {
        if (!action.listingId) {
          return [ListingActions.listingSelected({ listing: undefined })];
        }

        const id = action.listingId;
        const foundListing = listings.find(
          (el: IListing) => (el.id && id && el.id === id) || (el.syncId && id && el.syncId === id),
        );
        if (foundListing) {
          return [ListingActions.listingSelected({ listing: foundListing })];
        }
        return [ListingActions.loadListing({ listingId: id })];
      }),
      catchError((err) =>
        of(
          ListingActions.loadListingFailed({
            error: err?.error?.message || err?.message || err || 'Error Selecting Listing',
          }),
        ),
      ),
    ),
  { functional: true },
);

export const createListingFailed$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ListingActions.createListingFailed),
      map((action) =>
        AlertActions.displayAlert({
          title: 'Error Creating Listing',
          body: stringFromError(action.error),
          level: 'error',
        }),
      ),
    ),
  { functional: true },
);

export const removeListingFailed$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ListingActions.removeListingFailed),
      map((action) =>
        AlertActions.displayAlert({
          title: 'Error Removing Listing',
          body: stringFromError(action.error),
          level: 'error',
        }),
      ),
    ),
  { functional: true },
);

export const loadListingFailed$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ListingActions.loadListingFailed),
      map((action) =>
        AlertActions.displayAlert({
          title: 'Error Loading Listings',
          body: stringFromError(action.error),
          level: 'error',
        }),
      ),
    ),
  { functional: true },
);

export const updateListingFailed$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ListingActions.updateListingFailed),
      map((action) =>
        AlertActions.displayAlert({
          title: 'Error Updating Listing',
          body: stringFromError(action.error),
          level: 'error',
        }),
      ),
    ),
  { functional: true },
);

export const listingEnabledFailed$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ListingActions.listingEnabledFailed),
      map((action) =>
        AlertActions.displayAlert({
          title: 'Error Enabling Listing',
          body: action.body?.split('\n').join('<br> - '),
          level: 'error',
          noTimeout: true,
        }),
      ),
    ),
  { functional: true },
);

export const listingDisabledFailed$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ListingActions.listingDisabledFailed),
      map((action) =>
        AlertActions.displayAlert({
          title: 'Error Disabling Listing',
          body: action.error?.split('\n').join('<br> - '),
          level: 'error',
        }),
      ),
    ),
  { functional: true },
);

export const favoriteListing$ = createEffect(
  (actions$ = inject(Actions), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.favoriteListing),
      switchMap((action) => {
        if (!action.point) {
          return [
            ListingActions.listingFavorFailed({
              error: 'No listing to favorite',
            }),
          ];
        }

        return listingService.favorite(action.point).pipe(
          mergeMap((successResult: { success: boolean; message: string }) => {
            if (!successResult.success) {
              return [
                ListingActions.listingFavorFailed({
                  error: successResult.message,
                }),
              ];
            }

            return [
              ListingActions.listingFavorCompleted({
                id: action.point.id || 0,
                favored: true,
              }),
            ];
          }),
          catchError((err) => [
            ListingActions.listingFavorFailed({
              error: err?.error?.message || err?.message || err || 'Error Favoriting Listing',
            }),
          ]),
        );
      }),
      catchError((err) => [
        ListingActions.listingFavorFailed({
          error: err?.error?.message || err?.message || err || 'Error Favoriting Listing',
        }),
      ]),
    ),
  { functional: true },
);

export const unfavoriteListing$ = createEffect(
  (actions$ = inject(Actions), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.unfavoriteListing),
      switchMap((action) => {
        if (!action.point) {
          return [
            ListingActions.listingFavorFailed({
              error: 'No listing to unfavorite',
            }),
          ];
        }

        return listingService.unfavorite(action.point).pipe(
          mergeMap((successResult: { success: boolean; message: string }) => {
            if (!successResult.success) {
              return [
                ListingActions.listingFavorFailed({
                  error: successResult.message,
                }),
              ];
            }

            return [ListingActions.listingFavorCompleted({ id: action.point.id || 0, favored: false })];
          }),
          catchError((err) => [
            ListingActions.listingFavorFailed({
              error: err?.error?.message || err?.message || err || 'Error Unfavoriting Listing',
            }),
          ]),
        );
      }),
      catchError((err) => [
        ListingActions.listingFavorFailed({
          error: err?.error?.message || err?.message || err || 'Error Unfavoriting Listing',
        }),
      ]),
    ),
  { functional: true },
);

export const listingFavorCompleted$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ListingActions.listingFavorCompleted),
      withLatestFrom(store.select(AuthSelectors.getUser)),
      switchMap(([action, currentUser]) => {
        if (currentUser && currentUser.id) {
          return [UserActions.loadUser({ id: currentUser.id, refId: undefined, force: true })];
        } else {
          return [NGRX_NO_ACTION];
        }
      }),
      catchError((err) => [
        ListingActions.listingFavorFailed({
          error: err?.error?.message || err?.message || err || 'Error Unfavoriting Listing',
        }),
      ]),
    ),
  { functional: true },
);

export const updateListingOnSuccess$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        ListingActions.updateLocationSuccess,
        ListingActions.updateExtrasSuccess,
        ListingActions.updatePoliciesSuccess,
        ListingActions.updateBookingPolicySuccess,
        ListingActions.updateBlackoutTimesSuccess,
        ListingActions.updatePricingSuccess,
        ListingActions.updateDescriptionSuccess,
        ListingActions.updatePropertyTypesSuccess,
        ListingActions.updatePotentialHazardsSuccess,
        SiteActions.updateAmenitiesAndExtrasSuccess,
      ),
      map((action) => {
        return ListingActions.updateListing({ listing: action.listing });
      }),
    ),
  { functional: true },
);

export const saveDescriptionStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.updateDescription),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([action, status]) => {
        if (status.connected) {
          if (action.listingId && action.data.name && action.data.description) {
            return listingService
              .updateDescription(action.listingId, action.data)
              .pipe(
                mergeMap((savedListing: IListing) => [
                  ListingActions.updateDescriptionSuccess({ listing: savedListing }),
                ]),
              );
          }
          return [ListingActions.updateDescriptionFailed({ error: 'Listing data is missing.' })];
        } else {
          if (action.listingId) {
            // @todo Refactor offline features
            // if (!listing?.syncId) {
            //   listing = { ...listing, syncId: uuidv4() };
            // }
            // listing = new Listing({ ...listing, ...action});
            const actions: Action[] = [
              // ListingActions.updateDescriptionSuccess({ listing }),
              // NetworkActions.pendingListingUpdate({ listing }),
            ];
            return actions;
          }
          return [ListingActions.updateDescriptionFailed({ error: 'Site data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const savePropertyTypeStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.updatePropertyTypes),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([action, status]) => {
        if (status.connected) {
          if (action.listingId && Array.isArray(action.types)) {
            return listingService
              .updatePropertyType(action.listingId, action.types)
              .pipe(mergeMap((listing: IListing) => [ListingActions.updatePropertyTypesSuccess({ listing })]));
          }
          return [ListingActions.updatePropertyTypesFailed({ error: 'Listing data is missing.' })];
        } else {
          if (action.listingId) {
            // @todo Refactor offline features
            let listing: IListing = new Listing();
            // if (!listing?.syncId) {
            //   listing = { ...listing, syncId: uuidv4() };
            // }
            listing = new Listing({ id: action.listingId, ...action });
            const actions: Action[] = [
              ListingActions.updatePropertyTypesSuccess({ listing }),
              NetworkActions.pendingListingUpdate({ listing }),
            ];
            return actions;
          }
          return [ListingActions.updatePropertyTypesFailed({ error: 'Site data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const savePotentialHazardsStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.updatePotentialHazards),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([action, status]) => {
        if (status.connected) {
          if (
            action.listingId &&
            (action.potentialHazards || action.potentialHazards === false) &&
            (action.potentialHazardDescription || action.potentialHazardDescription === '')
          ) {
            return listingService
              .updatePotentialHazards(action.listingId, action.potentialHazards, action.potentialHazardDescription)
              .pipe(mergeMap((listing: IListing) => [ListingActions.updatePotentialHazardsSuccess({ listing })]));
          }
          return [ListingActions.updatePotentialHazardsFailed({ error: 'Listing data is missing.' })];
        } else {
          if (action.listingId) {
            // @todo Refactor offline features
            let listing: IListing = new Listing();
            listing = new Listing({ id: action.listingId, ...action });
            const actions: Action[] = [
              ListingActions.updatePotentialHazardsSuccess({ listing }),
              NetworkActions.pendingListingUpdate({ listing }),
            ];
            return actions;
          }
          return [ListingActions.updatePotentialHazardsFailed({ error: 'Listing data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const saveListingLocationStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.updateLocation),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([action, status]) => {
        if (status.connected) {
          if (action.listingId && action.data.coordinates) {
            return listingService
              .updateLocation(action.listingId, action.data)
              .pipe(
                mergeMap((savedListing: IListing) => [ListingActions.updateLocationSuccess({ listing: savedListing })]),
              );
          }
          return [ListingActions.updateLocationFailed({ error: 'Listing data is missing.' })];
        } else {
          if (action.listingId) {
            let listing = new Listing();
            // if (!listing?.syncId) {
            //   listing = { ...listing, syncId: uuidv4() };
            // }
            const actions: Action[] = [
              ListingActions.updateLocationSuccess({ listing }),
              NetworkActions.pendingListingUpdate({ listing }),
            ];
            return actions;
          }
          return [ListingActions.updateLocationFailed({ error: 'Listing data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const updatePolicies$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.updatePolicies),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([action, status]) => {
        if (status.connected) {
          if (action.listingId && action.policies) {
            return listingService
              .updatePolicies(action.listingId, action.policies)
              .pipe(
                mergeMap((savedListing: IListing) => [ListingActions.updatePoliciesSuccess({ listing: savedListing })]),
              );
          }
          return [ListingActions.updatePoliciesFailed({ error: 'Listing data is missing.' })];
        } else {
          // @todo add offline feature
          if (action.listingId) {
            let listing = new Listing({ id: action.listingId });
            if (!listing?.syncId) {
              listing.syncId = uuidv4();
            }
            const actions: Action[] = [
              ListingActions.updatePoliciesSuccess({ listing }),
              NetworkActions.pendingListingUpdate({ listing }),
            ];
            return actions;
          }
          return [ListingActions.updatePoliciesFailed({ error: 'Listing data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const saveListingBookingStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.updateBookingPolicy),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([action, status]) => {
        if (status.connected) {
          if (action.listingId) {
            return listingService
              .updateBooking(action.listingId, action.data)
              .pipe(
                mergeMap((savedListing: IListing) => [
                  ListingActions.updateBookingPolicySuccess({ listing: savedListing }),
                ]),
              );
          }
          return [ListingActions.updateBookingPolicyFailed({ error: 'Listing data is missing.' })];
        } else {
          if (action.listingId) {
            // @todo Refactor offline features
            let listing = new Listing({ id: action.listingId });
            if (!listing?.syncId) {
              listing.syncId = uuidv4();
            }
            listing = new Listing({ ...listing, ...action });
            const actions: Action[] = [
              ListingActions.updateBookingPolicySuccess({ listing }),
              NetworkActions.pendingListingUpdate({ listing }),
            ];
            return actions;
          }
          return [ListingActions.updateBookingPolicyFailed({ error: 'Listing data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const updateExtras$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.updateExtras),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([action, status]) => {
        if (status.connected) {
          if (action.listingId && action.extras) {
            return listingService
              .updateExtras(action.listingId, action.extras)
              .pipe(
                mergeMap((savedListing: IListing) => [ListingActions.updateExtrasSuccess({ listing: savedListing })]),
              );
          }
          return [ListingActions.updateExtrasFailed({ error: 'Listing data is missing.' })];
        } else {
          // @todo refactor offline features
          if (action.listingId) {
            let listing = new Listing({ id: action.listingId });
            if (!listing?.syncId) {
              listing.syncId = uuidv4();
            }
            const actions: Action[] = [
              ListingActions.updateExtrasSuccess({ listing }),
              NetworkActions.pendingListingUpdate({ listing }),
            ];
            return actions;
          }
          return [ListingActions.updateExtrasFailed({ error: 'Listing data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const updateBlackoutTimes$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.updateBlackoutTimes),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([action, status]) => {
        if (status.connected) {
          if (action.listingId && action.blackoutTimes) {
            return listingService
              .updateBlackoutTimes(action.listingId, action.blackoutTimes)
              .pipe(
                mergeMap((savedListing: IListing) => [
                  ListingActions.updateBlackoutTimesSuccess({ listing: savedListing }),
                ]),
              );
          }
          return [ListingActions.updateBlackoutTimesFailed({ error: 'Listing data is missing.' })];
        } else {
          // @todo refactor offline features
          if (action.listingId) {
            let listing = new Listing({ id: action.listingId });
            if (!listing?.syncId) {
              listing.syncId = uuidv4();
            }
            const actions: Action[] = [
              ListingActions.updateBlackoutTimesSuccess({ listing }),
              NetworkActions.pendingListingUpdate({ listing }),
            ];
            return actions;
          }
          return [ListingActions.updateBlackoutTimesFailed({ error: 'Listing data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const updatePricing$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.updatePricing),
      withLatestFrom(store.select(ListingSelectors.getSelectedListing)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[action, listing], status]) => {
        if (status.connected) {
          if (
            listing?.id &&
            (typeof action.nextReservationDiscount === 'number' ||
              typeof action.weekdayDiscount === 'number' ||
              action.cancellationPolicy)
          ) {
            return listingService
              .updateDiscount(listing?.id, action)
              .pipe(
                mergeMap((savedListing: IListing) => [ListingActions.updatePricingSuccess({ listing: savedListing })]),
              );
          }

          return [ListingActions.updatePricingFailed({ error: 'Listing data is missing.' })];
        } else {
          if (listing) {
            // @todo Refactor offline features
            if (!listing?.syncId) {
              listing = new Listing({ ...listing, syncId: uuidv4() });
            }
            listing = new Listing({ ...listing, ...action });
            const actions: Action[] = [
              ListingActions.updatePricingSuccess({ listing }),
              NetworkActions.pendingListingUpdate({ listing }),
            ];
            return actions;
          }
          return [ListingActions.updatePricingFailed({ error: 'listing data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const addNewSite$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingActions.addNewSite),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([action, status]) => {
        if (status.connected) {
          if (action.listing && action.listing.id) {
            return listingService.addNewSites(action.listing.id, 1).pipe(
              mergeMap((updatedListing: IListing) => [
                ListingActions.addNewSiteSuccess({ listing: updatedListing }),
                ListingActions.selectListing({ listingId: updatedListing.id }),
              ]),
              catchError((error) => [ListingActions.addNewSiteFailed({ error })]),
            );
          }
          return [ListingActions.addNewSiteFailed({ error: 'Listing data is missing.' })];
        } else {
          if (action.listing && action.listing.id) {
            // @todo Refactor offline features
            return [
              ListingActions.addNewSiteSuccess({
                listing: {
                  /* @todo Complete the new site */
                },
              }),
              ListingActions.selectListing({ listingId: action.listing.id }),
            ];
          }
          return [ListingActions.addNewSiteFailed({ error: 'Site data is missing.' })];
        }
      }),
    ),
  { functional: true },
);
